import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, Loader2, Search, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { localizedName } from '@/components/localized-name';
import {
  DEFAULT_OFFLINE_METHOD,
  OFFLINE_PAYMENT_METHODS,
  paymentMethodLabelKey,
} from '@/features/events/payment-methods';
import type { MembershipPlan } from '@/features/memberships/types';
import { formatCurrency, formatDate, initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useChangeSubscriptionPlan,
  useCreateSubscription,
  useSubscriptionPlans,
  useUpdateSubscription,
  useUserSearch,
} from './subscriptions.api';
import { ORIGIN, RECURRENCE_LENGTH, type SubscriptionRow } from './types';

/**
 * Create or edit a subscription.
 *
 * Two things differ from the drawer this replaces:
 *
 * 1. "How it was paid" is an explicit three-way choice. The old form had a bare
 *    "was sponsored" toggle that silently decided whether the subscription
 *    counted as revenue, and no way at all to say what was actually collected.
 * 2. A live sentence at the bottom restates the whole thing in words, so the
 *    person clicking Create can check it without re-reading six controls.
 */

const PAID_RECEIVED = 'received';
const PAID_SPONSORED = 'sponsored';
const PAID_NOT_YET = 'not_paid';
type PaidMode = typeof PAID_RECEIVED | typeof PAID_SPONSORED | typeof PAID_NOT_YET;

/** `<input type="date">` wants `yyyy-MM-dd`; the API sends ISO timestamps. */
function toDateInput(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(/[zZ]$/.test(value) ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

const today = () => new Date().toISOString().slice(0, 10);

/** Sent as local midnight / end of day, matching what the original posted. */
const startOfDay = (value: string) => `${value}T00:00:00.000`;
const endOfDay = (value: string) => `${value}T23:59:59.000`;

/** Where one period of this plan ends, given where it starts. */
function periodEnd(start: string, recurrence: number | null | undefined) {
  const step = RECURRENCE_LENGTH[recurrence ?? 0];
  if (!start || !step) return start;
  const [amount, unit] = step;
  const date = new Date(`${start}T00:00:00`);
  if (unit === 'day') date.setDate(date.getDate() + amount);
  if (unit === 'month') date.setMonth(date.getMonth() + amount);
  if (unit === 'year') date.setFullYear(date.getFullYear() + amount);
  return date.toISOString().slice(0, 10);
}

/** "3 months", "28 days" — long enough to notice a plan that runs a year. */
function durationLabel(start: string, end: string, t: (k: string, o?: never) => string) {
  if (!start || !end) return '';
  const days = Math.round(
    (new Date(`${end}T00:00:00`).getTime() -
      new Date(`${start}T00:00:00`).getTime()) /
      86_400_000
  );
  if (days <= 0) return '';
  if (days % 365 === 0) {
    return `${days / 365} ${t('manageSubscriptions.years')}`;
  }
  if (days >= 28 && days % 30 < 3) {
    return `${Math.round(days / 30)} ${t('manageSubscriptions.months')}`;
  }
  return `${days} ${t('manageSubscriptions.days')}`;
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-primary text-xs font-semibold tracking-wider uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <Label className="text-muted-foreground text-sm font-normal">
        {label}
      </Label>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

export function SubscriptionFormSheet({
  row,
  open,
  onOpenChange,
  onSaved,
}: {
  /** `null` closes the sheet; an empty object opens it in create mode. */
  row: SubscriptionRow | Record<string, never> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (newId?: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const existing = (row && 'id' in row ? row : null) as SubscriptionRow | null;
  const isEdit = !!existing?.id;

  const { data: plans, isPending: loadingPlans } = useSubscriptionPlans();
  const create = useCreateSubscription();
  const update = useUpdateSubscription();
  const changePlan = useChangeSubscriptionPlan();
  const userSearch = useUserSearch();

  const saving = create.isPending || update.isPending || changePlan.isPending;
  const [error, setError] = useState<string | null>(null);

  // who
  const [userQuery, setUserQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    completeName: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    avatar: string | null;
  } | null>(null);

  // what
  const [planId, setPlanId] = useState('');
  const [newPlanId, setNewPlanId] = useState('');

  // when
  const [overrideDates, setOverrideDates] = useState(false);
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());

  // how it was paid
  const [paidMode, setPaidMode] = useState<PaidMode>(PAID_NOT_YET);
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState(String(DEFAULT_OFFLINE_METHOD));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  // options
  const [countForLimit, setCountForLimit] = useState(true);
  const [allowPromo, setAllowPromo] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [wasSponsored, setWasSponsored] = useState(false);

  const selectedPlan = useMemo(
    () => (plans ?? []).find((plan) => plan.id === planId) ?? null,
    [plans, planId]
  );

  // Reset whenever the sheet opens, so a create never inherits an edit's state.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setNewPlanId('');
    if (isEdit && existing) {
      setStartDate(toDateInput(existing.startDate) || today());
      setEndDate(toDateInput(existing.endDate) || today());
      setIsActive(existing.isActive);
      setWasSponsored(existing.origin === ORIGIN.sponsored);
      setCountForLimit(existing.countForLimit);
    } else {
      setUserQuery('');
      setSelectedUser(null);
      setPlanId('');
      setOverrideDates(false);
      setStartDate(today());
      setEndDate(today());
      setPaidMode(PAID_NOT_YET);
      setAmount(null);
      setMethod(String(DEFAULT_OFFLINE_METHOD));
      setReference('');
      setNotes('');
      setCountForLimit(true);
      setAllowPromo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  /*
   * Pre-fill the amount with what the plan currently costs, promo included, and
   * let it be overwritten — overwriting it is exactly how a negotiated rate gets
   * recorded. The end date follows the plan's recurrence unless it was set by
   * hand.
   */
  useEffect(() => {
    if (!selectedPlan || isEdit) return;
    const price =
      selectedPlan.isOnPromo && selectedPlan.promoPrice
        ? selectedPlan.promoPrice
        : selectedPlan.price;
    setAmount((current) => current ?? price ?? null);
    if (!overrideDates) {
      setEndDate(periodEnd(startDate, selectedPlan.recurrence));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // A start date the admin changed still has to move the end date with it.
  useEffect(() => {
    if (isEdit || overrideDates || !selectedPlan) return;
    setEndDate(periodEnd(startDate, selectedPlan.recurrence));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, overrideDates]);

  const planLabel = (plan: MembershipPlan) =>
    [
      localizedName(plan, language),
      plan.locationId ? ` @ ${localizedName(plan.location, language)}` : '',
      ` — ${formatCurrency(plan.price)}`,
      plan.isActive ? '' : ` (${t('manageSubscriptions.notActive')})`,
    ].join('');

  const sortedPlans = useMemo(
    () =>
      [...(plans ?? [])].sort((a, b) =>
        localizedName(a, language).localeCompare(
          localizedName(b, language),
          language,
          { sensitivity: 'base' }
        )
      ),
    [plans, language]
  );

  const lengthText = durationLabel(startDate, endDate, t);

  const canSave = isEdit
    ? !!startDate && !!endDate
    : !!selectedUser &&
      !!planId &&
      !!startDate &&
      !!endDate &&
      (paidMode !== PAID_RECEIVED || (amount ?? 0) > 0);

  function fail(e: unknown) {
    setError(
      e instanceof Error ? e.message : t('manageSubscriptions.actionFailed')
    );
  }

  function save() {
    setError(null);

    if (isEdit && existing) {
      update.mutate(
        {
          id: existing.id,
          startDate: startOfDay(startDate),
          endDate: endOfDay(endDate),
          isActive,
          wasSponsored,
          countForSubscriptionLimit: countForLimit,
        },
        {
          onSuccess: () => {
            toast.success(t('manageSubscriptions.saved'));
            onOpenChange(false);
            onSaved();
          },
          onError: fail,
        }
      );
      return;
    }

    create.mutate(
      {
        subscriptionId: planId,
        userId: selectedUser!.id,
        countForSubscriptionLimit: countForLimit,
        allowPromo,
        wasSponsored: paidMode === PAID_SPONSORED,
        startDate: startOfDay(startDate),
        endDate: endOfDay(endDate),
        // Only send payment fields when money actually changed hands: a zero
        // would create a meaningless payment row, while sending nothing leaves
        // the subscription unbilled, the honest state for "not paid yet".
        ...(paidMode === PAID_RECEIVED && (amount ?? 0) > 0
          ? {
              amountReceived: amount!,
              paymentMethod: Number(method),
              offlineReference: reference,
              adminNotes: notes,
            }
          : {}),
      },
      {
        onSuccess: (newId) => {
          toast.success(t('manageSubscriptions.created'));
          onOpenChange(false);
          onSaved(newId);
        },
        onError: fail,
      }
    );
  }

  function runChangePlan() {
    if (!existing || !newPlanId) return;
    setError(null);
    changePlan.mutate(
      { id: existing.id, newSubscriptionId: newPlanId },
      {
        onSuccess: () => {
          toast.success(t('manageSubscriptions.planChanged'));
          onOpenChange(false);
          onSaved();
        },
        onError: fail,
      }
    );
  }

  /** Everything the form is about to do, in one sentence. */
  const echo =
    !isEdit && selectedUser && selectedPlan ? (
      <div className="bg-accent text-accent-foreground rounded-xl border p-3 text-sm">
        <b>{selectedUser.completeName}</b>{' '}
        {t('manageSubscriptions.echoGets')}{' '}
        <b>{localizedName(selectedPlan, language)}</b>{' '}
        {t('manageSubscriptions.echoFrom')} <b>{formatDate(startDate)}</b>{' '}
        {t('manageSubscriptions.echoTo')} <b>{formatDate(endDate)}</b>
        {lengthText ? ` (${lengthText})` : ''}.
        {paidMode === PAID_RECEIVED && (amount ?? 0) > 0 && (
          <>
            {' '}
            {t('manageSubscriptions.echoRecordedAs')}{' '}
            <b>
              {formatCurrency(amount)}{' '}
              {t(paymentMethodLabelKey(Number(method))).toLowerCase()}
            </b>
            .
            {(amount ?? 0) < (selectedPlan.price ?? 0) && (
              <>
                {' '}
                {t('manageSubscriptions.echoListPriceIs')}{' '}
                {formatCurrency(selectedPlan.price)},{' '}
                {t('manageSubscriptions.echoLoggedAsDiscount', {
                  amount: formatCurrency((selectedPlan.price ?? 0) - (amount ?? 0)),
                })}
              </>
            )}
          </>
        )}
        {paidMode === PAID_SPONSORED && <> {t('manageSubscriptions.echoSponsored')}</>}
        {paidMode === PAID_NOT_YET && <> {t('manageSubscriptions.echoNotPaid')}</>}
      </div>
    ) : null;

  const paidModes: { value: PaidMode; label: string }[] = [
    { value: PAID_RECEIVED, label: t('manageSubscriptions.paidModeReceived') },
    { value: PAID_SPONSORED, label: t('manageSubscriptions.paidModeSponsored') },
    { value: PAID_NOT_YET, label: t('manageSubscriptions.paidModeNotYet') },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {isEdit
              ? t('manageSubscriptions.editSubscription')
              : t('manageSubscriptions.newSubscription')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 p-4">
          {error && (
            <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-3 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {!isEdit && (
            <>
              <Section title={t('manageSubscriptions.who')}>
                {selectedUser ? (
                  <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
                    <Avatar className="size-9 shrink-0">
                      {selectedUser.avatar && (
                        <AvatarImage src={selectedUser.avatar} alt="" />
                      )}
                      <AvatarFallback>
                        {initials(selectedUser.firstName, selectedUser.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">
                        {selectedUser.completeName}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {selectedUser.email}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={saving}
                      aria-label={t('manageSubscriptions.remove')}
                      onClick={() => setSelectedUser(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <form
                      role="search"
                      className="flex gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (userQuery.trim()) userSearch.mutate(userQuery.trim());
                      }}
                    >
                      <div className="relative flex-1">
                        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                        <Input
                          value={userQuery}
                          disabled={saving}
                          onChange={(e) => setUserQuery(e.target.value)}
                          placeholder={t('manageSubscriptions.searchPlaceholder')}
                          aria-label={t('manageSubscriptions.searchPlaceholder')}
                          className="pl-9"
                        />
                      </div>
                      <Button type="submit" disabled={userSearch.isPending}>
                        {userSearch.isPending ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          t('common.search')
                        )}
                      </Button>
                    </form>

                    {userSearch.isPending && (
                      <div className="divide-y rounded-xl border">
                        {Array.from({ length: 3 }).map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2.5 p-2.5"
                          >
                            <Skeleton className="size-8 shrink-0 rounded-full" />
                            <div className="flex-1 space-y-1.5">
                              <Skeleton className="h-4 w-40" />
                              <Skeleton className="h-3 w-52" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {userSearch.isSuccess && userSearch.data.length === 0 && (
                      <p className="text-muted-foreground text-xs">
                        {t('manageSubscriptions.noUsersFound')}
                      </p>
                    )}

                    {(userSearch.data ?? []).length > 0 && (
                      <div className="max-h-56 divide-y overflow-y-auto rounded-xl border">
                        {userSearch.data!.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left transition-colors"
                            onClick={() => {
                              setSelectedUser(user);
                              userSearch.reset();
                              setUserQuery('');
                            }}
                          >
                            <Avatar className="size-8 shrink-0">
                              {user.avatar && (
                                <AvatarImage src={user.avatar} alt="" />
                              )}
                              <AvatarFallback>
                                {initials(user.firstName, user.lastName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="truncate text-sm">
                                {user.completeName}
                              </p>
                              <p className="text-muted-foreground truncate text-xs">
                                {user.email}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Section>

              <Section title={t('manageSubscriptions.whichPlan')}>
                {loadingPlans ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <Select
                    value={planId}
                    disabled={loadingPlans || saving}
                    onValueChange={(value) => {
                      setPlanId(value);
                      setAmount(null);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t('manageSubscriptions.selectPlan')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {planLabel(plan)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Section>
            </>
          )}

          <Section title={t('manageSubscriptions.when')}>
            {!isEdit && (
              <Toggle
                label={t('manageSubscriptions.overrideDates')}
                checked={overrideDates}
                disabled={saving}
                onChange={setOverrideDates}
              />
            )}
            {isEdit || overrideDates ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">
                    {t('manageSubscriptions.startDate')}
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    disabled={saving}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">
                    {t('manageSubscriptions.endDate')}
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    disabled={saving}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                {formatDate(startDate)} – {formatDate(endDate)}
                {lengthText ? ` (${lengthText})` : ''}
              </p>
            )}
          </Section>

          {!isEdit && (
            <Section title={t('manageSubscriptions.howItWasPaid')}>
              <div className="space-y-1">
                {paidModes.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    disabled={saving}
                    onClick={() => setPaidMode(mode.value)}
                    className={cn(
                      'hover:bg-accent w-full rounded-lg border p-2.5 text-left text-sm transition-colors',
                      paidMode === mode.value && 'border-primary bg-accent'
                    )}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              {paidMode === PAID_RECEIVED && (
                <div className="border-primary/40 mt-2 space-y-3 border-l-2 py-1 pl-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">
                        {t('manageSubscriptions.amount')}
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={saving}
                        value={amount ?? ''}
                        onChange={(e) =>
                          setAmount(
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">
                        {t('manageSubscriptions.method')}
                      </Label>
                      <Select
                        value={method}
                        onValueChange={setMethod}
                        disabled={saving}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OFFLINE_PAYMENT_METHODS.map((value) => (
                            <SelectItem key={value} value={String(value)}>
                              {t(paymentMethodLabelKey(value))}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">
                      {t('manageSubscriptions.reference')}
                    </Label>
                    <Input
                      value={reference}
                      disabled={saving}
                      onChange={(e) => setReference(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">
                      {t('manageSubscriptions.adminNotes')}
                    </Label>
                    <Textarea
                      rows={2}
                      value={notes}
                      disabled={saving}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </Section>
          )}

          <Section title={t('manageSubscriptions.options')}>
            {isEdit && (
              <>
                <Toggle
                  label={t('manageSubscriptions.isActive')}
                  checked={isActive}
                  disabled={saving}
                  onChange={setIsActive}
                />
                <Toggle
                  label={t('manageSubscriptions.wasSponsored')}
                  checked={wasSponsored}
                  disabled={saving}
                  onChange={setWasSponsored}
                />
              </>
            )}
            <Toggle
              label={t('manageSubscriptions.countForLimit')}
              checked={countForLimit}
              disabled={saving}
              onChange={setCountForLimit}
            />
            {!isEdit && selectedPlan?.isOnPromo && (
              <Toggle
                label={t('manageSubscriptions.allowPromo')}
                checked={allowPromo}
                disabled={saving}
                onChange={setAllowPromo}
              />
            )}
          </Section>

          {isEdit && (
            <Section title={t('manageSubscriptions.changePlan')}>
              {loadingPlans ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <Select
                  value={newPlanId}
                  disabled={loadingPlans || saving}
                  onValueChange={setNewPlanId}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t('manageSubscriptions.selectPlan')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedPlans
                      .filter((plan) => plan.id !== existing?.subscriptionId)
                      .map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {localizedName(plan, language)} —{' '}
                          {formatCurrency(plan.price)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
              {/* Its own endpoint, so its own button: changing the plan is not
                  part of saving the dates. */}
              <Button
                variant="outline"
                size="sm"
                disabled={!newPlanId || saving}
                onClick={runChangePlan}
              >
                {changePlan.isPending && <Loader2 className="animate-spin" />}
                {t('manageSubscriptions.changePlan')}
              </Button>
            </Section>
          )}

          {echo}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || saving} onClick={save}>
              {(create.isPending || update.isPending) && (
                <Loader2 className="animate-spin" />
              )}
              {isEdit
                ? t('common.save')
                : t('manageSubscriptions.createSubscription')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
